"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useMemo, useEffect } from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function BlogGridConsumer({
  selectedCategory
}: {
  selectedCategory: string;
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [currentPage, setCurrentPage] = useState(1);
  const filteredPosts = useMemo(() => {
    if (!posts) return [];
    if (selectedCategory === "All") return posts;
    return posts.filter(post => post.categories?.some(cat => cat.name === selectedCategory));
  }, [posts, selectedCategory]);
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);
  const postsPerPage = 9;
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIdx = (currentPage - 1) * postsPerPage;
  const slicedPosts = filteredPosts.slice(startIdx, startIdx + postsPerPage);
  const showPagination = totalPages > 1;
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div data-index={i} key={i}>
            <Card className="flex h-full flex-col overflow-hidden border-none shadow-md">
              <AspectRatio ratio={4 / 3} className="bg-muted animate-pulse" />
              <CardContent className="flex flex-1 flex-col p-6">
                <div className="mb-3 h-4 w-24 animate-pulse rounded-md bg-muted" />
                <div className="mb-3 h-6 w-full animate-pulse rounded-md bg-muted" />
                <div className="mb-4 h-4 w-3/4 animate-pulse rounded-md bg-muted" />
                <div className="mt-auto pt-4">
                  <div className="h-4 w-28 animate-pulse rounded-md bg-muted" />
                </div>
              </CardContent>
            </Card>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
        <p className="text-muted-foreground font-default">Failed to load articles. Please try again later.</p>
      </div>;
  }
  if (!filteredPosts || filteredPosts.length === 0) {
    return <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
        <p className="text-muted-foreground font-default">No articles found for this category.</p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {slicedPosts.map((post, i) => <div data-index={i} key={post.id}>
            <Card className="group flex h-full flex-col overflow-hidden border-none bg-card shadow-md transition-all duration-300 hover:shadow-xl">
              <div className="relative overflow-hidden">
                <AspectRatio ratio={4 / 3}>
                  <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668703/img/insight_archive_placeholder.png"} data-wvc-srcset={post.featuredImage ? undefined : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668703/img/insight_archive_placeholder.png-wvc-srcset"} data-wvc-sizes={post.featuredImage ? undefined : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668703/img/insight_archive_placeholder.png-wvc-sizes"} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </AspectRatio>
                {post.categories && post.categories.length > 0 && <Badge className="absolute left-4 top-4 border-none bg-primary/90 text-primary-foreground font-default backdrop-blur-sm hover:bg-primary">
                    {post.categories[0].name}
                  </Badge>}
              </div>
              <CardContent className="flex flex-1 flex-col p-6">
                <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground font-default">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>
                    {post.date ? new Date(post.date).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                }) : "Recent"}
                  </span>
                </div>
                <h3 className="font-default mb-3 line-clamp-2 text-xl font-bold text-foreground">
                  {post.title}
                </h3>
                {post.excerpt && <div className="mb-6 line-clamp-3 flex-1 text-muted-foreground font-default" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}
                <div className="mt-auto border-t border-border/50 pt-4">
                  <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-semibold text-primary transition-colors hover:text-primary/80 font-default">
                    Read Article
                    <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>)}
      </div>

      {showPagination && <div className="mt-16 flex flex-wrap items-center justify-center gap-2">
          {Array.from({
        length: totalPages
      }, (_, i) => i + 1).map((page, idx) => <button key={page} data-index={idx} onClick={() => {
        setCurrentPage(page);
        window.scrollTo({
          top: document.getElementById("full-blog-archive")?.offsetTop || 0,
          behavior: "smooth"
        });
      }} className={`flex h-10 w-10 items-center justify-center rounded-md transition-colors font-medium ${currentPage === page ? "bg-primary text-primary-foreground font-bold shadow-sm" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}>
              {page}
            </button>)}
        </div>}
    </>;
}
function CategoryFilter({
  selectedCategory,
  setSelectedCategory
}: {
  selectedCategory: string;
  setSelectedCategory: (cat: string) => void;
}) {
  const {
    posts
  } = usePosts();
  const categories = useMemo(() => {
    const cats = new Set<string>();
    cats.add("All");
    posts?.forEach(post => {
      post.categories?.forEach(cat => cats.add(cat.name));
    });
    return Array.from(cats);
  }, [posts]);
  return <div className="mb-8 flex flex-wrap gap-2">
      {categories.map((cat, i) => <Badge data-index={i} key={cat} variant={selectedCategory === cat ? "default" : "secondary"} className="cursor-pointer px-4 py-1.5 text-sm transition-colors hover:opacity-90" onClick={() => setSelectedCategory(cat)}>
          {cat}
        </Badge>)}
    </div>;
}
export default function BlogAndResources() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const wp_query_params = {
    post_type: "post",
    per_page: 100,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="257421" id="full-blog-archive" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-default mb-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl">최신 인사이트 리포트</h2>
            
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 100,
        order: "desc",
        orderby: "date"
      }}>
          <CategoryFilter selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} />
          <BlogGridConsumer selectedCategory={selectedCategory} />
        </PostsProvider>
      </div>
    </section>;
}